<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <div class="sr-page">
          <div class="sr-head">
            <div>
              <h2>Showcase recordings — {{ adminYear }}</h2>
              <p class="muted">
                Pieces and recordings on dancer <strong>IABF history</strong>.
                Cast is people from the directory — add or remove dancers, then save.
              </p>
            </div>
            <div class="sr-head-actions">
              <v-btn small outlined :loading="loading" @click="reload">Reload</v-btn>
              <v-btn small color="primary" @click="addRow">+ Add piece</v-btn>
              <v-btn
                small
                color="success"
                :loading="saving"
                :disabled="!dirty"
                @click="save"
              >
                Save changes
              </v-btn>
            </div>
          </div>

          <div class="sr-summary">
            <div class="sr-stat">
              <div class="sr-stat-num">{{ rows.length }}</div>
              <div class="sr-stat-lab">Pieces</div>
            </div>
            <div class="sr-stat">
              <div class="sr-stat-num">{{ withRecordingCount }}</div>
              <div class="sr-stat-lab">With recording</div>
            </div>
          </div>

          <p v-if="statusMsg" class="sr-status" :class="{ err: statusIsError }">{{ statusMsg }}</p>

          <div v-if="loading && !rows.length" class="muted">Loading…</div>

          <div v-else class="sr-table-wrap">
            <table class="sr-table">
              <thead>
                <tr>
                  <th style="width:3rem">#</th>
                  <th style="min-width:12rem">Piece title</th>
                  <th style="min-width:16rem">Cast</th>
                  <th style="min-width:14rem">Recording URL</th>
                  <th style="width:4.5rem">Watch</th>
                  <th style="width:4rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in rows" :key="r._key">
                  <td class="muted">{{ i + 1 }}</td>
                  <td><input v-model="r.title" class="sr-input" @input="markDirty"></td>
                  <td>
                    <div class="sr-cast">
                      <span
                        v-for="p in r.people"
                        :key="p.id"
                        class="sr-chip"
                      >
                        {{ p.label }}
                        <button type="button" class="sr-chip-x" @click="removePerson(r, p.id)">×</button>
                      </span>
                      <span
                        v-for="(n, ni) in r.extraNames"
                        :key="'x-' + ni"
                        class="sr-chip sr-chip--extra"
                        title="Not in people — display only"
                      >
                        {{ n }}
                        <button type="button" class="sr-chip-x" @click="removeExtra(r, ni)">×</button>
                      </span>
                      <AdminPersonPicker
                        :people="people"
                        :exclude-ids="personIdsOf(r)"
                        @select="addPerson(r, $event)"
                      />
                    </div>
                  </td>
                  <td><input v-model="r.recordingUrl" class="sr-input" type="url" @input="markDirty"></td>
                  <td>
                    <a
                      v-if="r.recordingUrl"
                      :href="r.recordingUrl"
                      target="_blank"
                      rel="noopener"
                      class="sr-watch"
                    >Open</a>
                    <span v-else class="muted">—</span>
                  </td>
                  <td>
                    <button type="button" class="sr-del" title="Remove" @click="removeRow(i)">✕</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!rows.length" class="muted sr-empty">
              No showcase archive for {{ adminYear }} yet. Click <strong>+ Add piece</strong>.
            </p>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import AdminPersonPicker from '@/components/iabf/AdminPersonPicker.vue'

let keySeq = 0
function emptyRow () {
  return {
    _key: 'n' + (++keySeq),
    id: '',
    title: '',
    people: [],
    extraNames: [],
    recordingUrl: ''
  }
}

function nameKey (s) {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim()
}

function extraCastNames (names, people) {
  const linked = {}
  ;(people || []).forEach(p => {
    linked[nameKey(p.label)] = true
  })
  return (names || []).filter(n => {
    const k = nameKey(n)
    return k && !linked[k]
  })
}

export default {
  name: 'ShowcaseRecordingsAdmin',
  components: { AdminPersonPicker },
  data () {
    return {
      loading: false,
      saving: false,
      rows: [],
      people: [],
      dirty: false,
      statusMsg: '',
      statusIsError: false
    }
  },
  computed: {
    adminYear () {
      return this.$store.getters.adminFestivalYear
    },
    withRecordingCount () {
      return this.rows.filter(r => (r.recordingUrl || '').trim()).length
    }
  },
  watch: {
    adminYear: {
      immediate: true,
      handler () {
        this.reload()
      }
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
  },
  methods: {
    markDirty () {
      this.dirty = true
      this.statusMsg = ''
    },
    personIdsOf (row) {
      return (row.people || []).map(p => p.id)
    },
    personById (id) {
      return this.people.find(p => p.id === id) || null
    },
    rowFromPiece (p) {
      const ids = Array.isArray(p.dancerPersonIds) ? p.dancerPersonIds.filter(Boolean) : []
      const people = ids.map(id => {
        const hit = this.personById(id)
        return hit || { id, label: id, firstName: '', lastName: '', email: '' }
      })
      return {
        _key: 's' + (++keySeq),
        id: p.id || '',
        title: p.title || '',
        people,
        extraNames: extraCastNames(p.dancerNames, people),
        recordingUrl: p.recordingUrl || ''
      }
    },
    addPerson (row, person) {
      if (!row || !person || !person.id) {
        return
      }
      if (this.personIdsOf(row).indexOf(person.id) !== -1) {
        return
      }
      row.people.push(person)
      row.extraNames = extraCastNames(row.extraNames, row.people)
      this.markDirty()
    },
    removePerson (row, id) {
      row.people = row.people.filter(p => p.id !== id)
      this.markDirty()
    },
    removeExtra (row, index) {
      row.extraNames.splice(index, 1)
      this.markDirty()
    },
    addRow () {
      this.rows.push(emptyRow())
      this.markDirty()
    },
    removeRow (i) {
      this.rows.splice(i, 1)
      this.markDirty()
    },
    async reload () {
      this.loading = true
      this.statusMsg = ''
      this.dirty = false
      try {
        const people = await this.$store.dispatch('loadIabfPeopleDirectory')
        this.people = people || []
        const saved = await this.$store.dispatch(
          'loadShowcaseHistoryForYear',
          this.adminYear
        )
        this.rows = (saved || []).map(p => this.rowFromPiece(p))
      } catch (e) {
        this.statusMsg = 'Could not load showcase archive.'
        this.statusIsError = true
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.saving = true
      this.statusMsg = ''
      try {
        const payload = this.rows.map(r => ({
          id: r.id,
          title: r.title,
          dancerPersonIds: this.personIdsOf(r),
          dancerNames: (r.people || []).map(p => p.label).concat(r.extraNames || []),
          recordingUrl: r.recordingUrl
        }))
        const saved = await this.$store.dispatch('saveShowcaseHistoryForYear', {
          year: this.adminYear,
          pieces: payload
        })
        const pieces = saved && saved.pieces ? saved.pieces : []
        this.rows = pieces.map(p => this.rowFromPiece(p))
        this.dirty = false
        this.statusMsg = 'Saved ' + this.rows.length + ' piece(s) for ' + this.adminYear + '.'
        this.statusIsError = false
      } catch (e) {
        console.warn(e)
        this.statusMsg = 'Save failed. Check admin permissions and try again.'
        this.statusIsError = true
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.sr-page {
  max-width: 1180px;
  padding-bottom: 48px;
}
.sr-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.sr-head h2 {
  margin: 0 0 6px;
}
.sr-head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.muted {
  color: #666;
  margin: 0;
}
.sr-summary {
  display: flex;
  gap: 16px;
  margin: 16px 0;
}
.sr-stat {
  background: #f7f7f8;
  border: 1px solid #e5e5e8;
  border-radius: 12px;
  padding: 12px 18px;
  min-width: 120px;
}
.sr-stat-num {
  font-size: 22px;
  font-weight: 700;
}
.sr-stat-lab {
  font-size: 12px;
  color: #777;
}
.sr-status {
  margin: 0 0 12px;
  font-size: 14px;
  color: #1a7a3c;
}
.sr-status.err {
  color: #b00020;
}
.sr-table-wrap {
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e5e5e8;
  border-radius: 12px;
}
.sr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sr-table th,
.sr-table td {
  text-align: left;
  padding: 8px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}
.sr-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #777;
  background: #fafafa;
  white-space: nowrap;
}
.sr-input {
  width: 100%;
  min-width: 8rem;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font: inherit;
  background: #fff;
}
.sr-input:focus {
  outline: none;
  border-color: #388b9d;
  box-shadow: 0 0 0 2px rgba(56, 139, 157, 0.2);
}
.sr-cast {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.sr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef6f6;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
}
.sr-chip--extra {
  background: #f4f1ea;
  color: #6b5c3e;
}
.sr-chip-x {
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0 0 0 2px;
  font-size: 14px;
  line-height: 1;
}
.sr-watch {
  color: #267E7D;
  font-weight: 700;
  text-decoration: none;
}
.sr-watch:hover {
  text-decoration: underline;
}
.sr-del {
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 6px;
}
.sr-del:hover {
  background: #fdecea;
  color: #b00020;
}
.sr-empty {
  padding: 16px 18px;
}
</style>
